/**
 * Is the Sideguide Helper here, and whose Skool account will it post under?
 *
 * Two sources, each trusted for what it actually knows:
 *
 *   SERVER  (GET /v1/devices — `@api/v1/outbox`) — that a Helper is paired, which
 *           account it posts as, and when it was last seen running. Authoritative
 *           and cross-browser. This is what decides "online / offline" — a Helper
 *           heartbeating anywhere WILL send.
 *   BRIDGE  (the extension's content-script, src/content/bridge.ts) — that the
 *           Helper is in THIS browser and whether the live skool.com session still
 *           matches the paired account. This only ever ADDS a warning
 *           (wrong-account / signed-out); it never decides offline.
 *
 * Why that split matters: the bridge is a best-effort page↔extension channel. A
 * missed reply means "don't know", NOT "not here" — so a silent bridge can never
 * produce a scary message on its own. We keep a LIVE signal (poll while visible,
 * re-ask on focus, expire after a grace window) instead of probing once and caching
 * the answer forever, which is what used to strand the panel on a stale miss.
 *
 * The bridge is read-only: we ask, it answers. Nothing here makes the Helper act,
 * and it never hands over its device token.
 *
 * ON /v1 (doc 04 §3.3 row 67). The device list is `GET /v1/devices`, which is
 * ACCOUNT-scoped — one pairing serves every community the owner holds, which is why
 * this store has never taken a `skoolId` and still does not. Two things came with
 * the move: the answer is the standard `{items, next_cursor, total}` envelope rather
 * than a bare JSON array, and every instant is UTC-aware where `GET /outbox/devices`
 * emitted a bare datetime (`parseTs` already read those correctly, so `last_seen_at`
 * did not move — see `HelperPresence.svelte`).
 */
import type { MemberRef } from '@api';
import { fetchDevices, type Device } from '@api';

const NS = '__sideguide';
const REQUEST = 'helper-status-request';
const RESPONSE = 'helper-status';
/** The send channel's names — `src/content/bridge.ts` owns the other end. */
const SEND_REQUEST = 'helper-outbox-request';
const SEND_RESPONSE = 'helper-outbox';

/** Re-ask the bridge this often while the tab is visible. Each ask makes the worker
 *  read the live Skool session, so this is deliberately unhurried. */
const POLL_MS = 20_000;
/** A bridge reply counts as "present" for this long. Comfortably longer than two
 *  poll intervals, so a single dropped ask never flips the UI to offline. */
const GRACE_MS = 50_000;
/** How often we re-evaluate freshness (cheap, no network) and poll if due. */
const TICK_MS = 5_000;
/** Don't refetch the device list more than this often on focus churn. */
const DEVICE_REFRESH_MS = 60_000;
/** `/v1/devices` is paginated (50 by default, 200 the server's ceiling) where
 *  `/outbox/devices` answered the whole list. We ask for the ceiling because this
 *  store's questions are "is ANY device live" and "which account posts": a truncated
 *  first page that happened to omit the live pairing would render "Helper not set
 *  up" at somebody who has one. Rows come back newest-first, so 200 is the whole
 *  history for any real account. */
const DEVICE_PAGE = 200;

/** The Skool account behind a paired device, resolved by the backend.
 *
 *  §2.1's `MemberRef`, which is the one member shape the whole v1 surface speaks —
 *  so it carries `external_id`, and `handle` is nullable where the legacy
 *  `/outbox/devices` row declared it a string. `postingAs()` below already returns a
 *  union whose other arm has a nullable handle, so its readers see no change. */
export type SkoolAccount = MemberRef;

/** One Helper installation, exactly as `GET /v1/devices` returns it. Aliased rather
 *  than restated: a second declaration of the same seven keys is how a client shape
 *  drifts from the route that feeds it. */
export type HelperDevice = Device;

/** What the extension reports about itself. Mirrors background.helperStatus(). */
interface BridgeStatus {
  installed: true;
  ready: boolean;
  version?: string;
  paired?: boolean;
  sideguide?: { email: string; name: string | null } | null;
  skoolIds?: number[];
  expiresAt?: number | null;
  pairedSkool?: { id: string } | null;
  liveSkool?: { id: string; handle: string | null; name: string | null; picture: string | null } | null;
  /** Whether the Helper could READ the Skool session, separately from what it found.
   *  ABSENT on installs older than this field — see `derive()`. */
  skoolSession?: 'signed-in' | 'signed-out' | 'unknown' | null;
  /** Null when it can't be determined (signed out, or paired before we recorded it). */
  matches?: boolean | null;
}

/**
 * What the UI branches on. The bridge only ever reports its OWN facts; a silent
 * bridge is `no-bridge` (presence unknown — defer to the server), never a guess
 * about another browser.
 */
export type HelperState =
  | 'unknown'              // no sync has completed yet
  | 'no-bridge'           // synced, but the bridge isn't answering here — unknown
  | 'installed-not-paired'
  | 'signed-out-of-skool'
  | 'skool-unreadable'    // the Helper is here but could not read the Skool session
  | 'account-mismatch'
  | 'ready';

export const helper = $state<{
  state: HelperState;
  /** Latest status the bridge reported (may be past its freshness window). */
  bridge: BridgeStatus | null;
  /** True while a bridge reply is within the grace window — i.e. the Helper is
   *  answering in THIS browser right now. */
  bridgeFresh: boolean;
  /** The bridge has answered at least once this session — i.e. the Helper WAS in
   *  this browser. If it later stops answering (bridgeFresh false), the extension
   *  was deleted/disabled here, which is a faster, surer "offline" than waiting out
   *  the server's last-seen staleness window. */
  sawBridge: boolean;
  /** Devices paired to the account, from the server. */
  devices: HelperDevice[];
  /** At least one sync (device fetch or bridge reply) has completed. */
  loaded: boolean;
}>({ state: 'unknown', bridge: null, bridgeFresh: false, sawBridge: false, devices: [], loaded: false });

/** The account a reply will be published under, best source first. */
export function postingAs(): SkoolAccount | { handle: string | null; name: string | null; picture_profile: string | null } | null {
  const paired = helper.devices.find((d) => !d.revoked_at && d.skool_account)?.skool_account;
  const live = helper.bridge?.liveSkool;
  // The server's answer names the account the Helper is REGISTERED as, which is
  // what actually posts. Fall back to the live session when it can't be resolved.
  if (paired) return paired;
  if (live) return { handle: live.handle, name: live.name, picture_profile: live.picture };
  return null;
}

// --- live sync ---------------------------------------------------------------

let started = false;
let lastReplyAt = 0;
let lastPollAt = 0;
let lastDeviceAt = 0;
let firstSync: Promise<void> | null = null;

function derive(): HelperState {
  const b = helper.bridge;
  if (b && helper.bridgeFresh) {
    if (!b.paired) return 'installed-not-paired';
    if (!b.liveSkool) {
      // "COULD NOT READ IT" IS NOT "SIGNED OUT" — the same discipline this file already
      // applies to a silent bridge, one level further in. An unreadable session was
      // being reported as a sign-out, which is a claim about the owner's browser that
      // one failed request is not enough to make.
      //
      // An install older than `skoolSession` does not send it, and for those a null
      // `liveSkool` has always meant signed out; keep saying so rather than
      // downgrading every one of them to "can't tell".
      return b.skoolSession === 'unknown' ? 'skool-unreadable' : 'signed-out-of-skool';
    }
    // `matches === null` means we can't tell (paired before the account was
    // recorded). Treat unknown as fine — the worker still refuses to post under the
    // wrong account, so nothing unsafe slips through.
    if (b.matches === false) return 'account-mismatch';
    return 'ready';
  }
  return helper.loaded ? 'no-bridge' : 'unknown';
}

/** Fold the current facts into `bridgeFresh` + `state`. Cheap; no network. */
function recompute(): void {
  helper.bridgeFresh = lastReplyAt > 0 && Date.now() - lastReplyAt < GRACE_MS;
  helper.state = derive();
}

function onMessage(event: MessageEvent): void {
  if (event.source !== window || event.origin !== window.location.origin) return;
  const data = event.data as Record<string, unknown> | null;
  if (!data || data[NS] !== RESPONSE) return;
  const payload = data['payload'] as BridgeStatus | null;
  if (!payload) return;
  helper.bridge = payload;
  helper.loaded = true;
  helper.sawBridge = true;
  lastReplyAt = Date.now();
  recompute();
}

function ask(): void {
  lastPollAt = Date.now();
  try {
    window.postMessage({ [NS]: REQUEST, nonce: null }, window.location.origin);
  } catch {
    /* opaque origin — nothing to poll, state simply stays 'no-bridge' */
  }
}

async function refreshDevices(): Promise<void> {
  lastDeviceAt = Date.now();
  try {
    helper.devices = (await fetchDevices({ limit: DEVICE_PAGE })).items;
  } catch {
    /* keep the last-known devices; a name lookup must never blank the UI */
  }
  helper.loaded = true;
  recompute();
}

function onVisible(): void {
  if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
  ask(); // snap presence back the moment the owner returns to the tab
  if (Date.now() - lastDeviceAt > DEVICE_REFRESH_MS) void refreshDevices();
}

/** Start the live sync. Idempotent and page-lifetime — safe to call from every
 *  component that shows Helper state; only the first call does the wiring. */
export function ensureHelper(): Promise<void> {
  if (started) return firstSync ?? Promise.resolve();
  started = true;
  if (typeof window !== 'undefined') {
    window.addEventListener('message', onMessage);
    window.addEventListener('focus', onVisible);
    document.addEventListener('visibilitychange', onVisible);
    ask(); // catch an already-idle extension that announced before we listened
    setInterval(() => {
      const visible = typeof document === 'undefined' || document.visibilityState === 'visible';
      if (visible) {
        if (Date.now() - lastPollAt >= POLL_MS) ask();
        // Keep the server-side last_seen fresh so online→offline flips on its own
        // even on a tab that's been open (and unfocused) for a while.
        if (Date.now() - lastDeviceAt >= DEVICE_REFRESH_MS) void refreshDevices();
      }
      recompute();
    }, TICK_MS);
  }
  firstSync = refreshDevices();
  return firstSync;
}

/**
 * Ask the Helper in this browser to send what was just queued, and wait for the answer.
 *
 * A reply written here cannot go straight to Skool — this page holds an admin session,
 * not the owner's Skool cookies — so it becomes an outbox row and a Helper posts it. The
 * wait used to be up to the alarm period, 30 seconds, with nothing to show for it but
 * the word "Queued". Everything needed to do better was already here: the bridge proves
 * a Helper is in THIS browser, and the worker needs no Skool tab to post (it carries the
 * session itself). So we ask, and report what actually happened.
 *
 * `null` means NOTHING IS KNOWN — no extension here, an older build without the channel,
 * a worker that never answered. It is not "it failed": the row is queued and the alarm
 * will send it, so the caller must fall back to exactly what it said before, never to an
 * error. Same rule the status channel follows above, for the same reason.
 *
 * The nonce is what makes this safe to await. The status channel broadcasts unprompted
 * announcements with `nonce: null`, so a reply has to be matched to its request rather
 * than to the next message that happens to arrive.
 */
export interface SendNowResult {
  /** The worker ran a drain. False when it was debounced or unreachable. */
  acted: boolean;
  sent: number;
  failed: number;
  /** Why nothing was sent, in the worker's own words: 'signed out of Skool',
   *  'wrong Skool account', 'not paired', 'nothing queued'. */
  skipped: string | null;
  lastError: string | null;
}

/** Long enough for a real send: the outgoing-content gate plus a Skool round trip,
 *  with room for a worker that has to boot first. Past this we simply do not know, and
 *  "don't know" falls back to the queued wording rather than to a scary one. */
const SEND_TIMEOUT_MS = 12_000;
let sendNonce = 0;

function sendQueuedNow(): Promise<SendNowResult | null> {
  if (typeof window === 'undefined') return Promise.resolve(null);
  const nonce = `sg-send-${++sendNonce}-${Date.now()}`;
  return new Promise((resolve) => {
    let done = false;
    const finish = (value: SendNowResult | null): void => {
      if (done) return;
      done = true;
      window.removeEventListener('message', onReply);
      clearTimeout(timer);
      resolve(value);
    };
    function onReply(event: MessageEvent): void {
      if (event.source !== window || event.origin !== window.location.origin) return;
      const data = event.data as Record<string, unknown> | null;
      if (!data || data[NS] !== SEND_RESPONSE || data['nonce'] !== nonce) return;
      const p = (data['payload'] ?? {}) as Record<string, unknown>;
      // A worker that refused to act tells us nothing about the reply's fate.
      if (p['acted'] !== true) return finish(null);
      finish({
        acted: true,
        sent: Number(p['sent'] ?? 0),
        failed: Number(p['failed'] ?? 0),
        skipped: (p['skipped'] as string | null) ?? null,
        lastError: (p['lastError'] as string | null) ?? null,
      });
    }
    const timer = setTimeout(() => finish(null), SEND_TIMEOUT_MS);
    window.addEventListener('message', onReply);
    try {
      window.postMessage({ [NS]: SEND_REQUEST, nonce }, window.location.origin);
    } catch {
      finish(null); // opaque origin — no bridge to talk to
    }
  });
}

/**
 * Queue is already done; get it SENT, and say which of the two happened.
 *
 * For a composer's `onSubmit` to return — the word it ticks is whatever comes back
 * (`packages/ui/src/composer/ReplyComposer.svelte`). Call it after the outbox row is
 * safely created, never instead of creating it: this only accelerates delivery, and a
 * failure here must never make a queued reply look lost.
 *
 * "Sent" REQUIRES `failed === 0`. The drain reports totals, not per-row fates, so a
 * batch of three that sent two and failed one cannot tell us which was ours — and the
 * one word this must never say wrongly is the one that means "it is on Skool". With
 * nothing failed, everything leased went out, ours included.
 *
 * Every other outcome is "Queued", deliberately, including the ones that are really
 * blocked: signed out of Skool, wrong Skool account, no Helper in this browser. The row
 * genuinely IS queued and the alarm will take it as soon as the obstacle clears, and the
 * reason is already on screen — `HelperPresence` renders exactly those states from the
 * same bridge. A composer is the wrong place to re-litigate them, and "Queued" is not a
 * lie about any of them.
 */
export async function askHelperToSend(): Promise<string> {
  const result = await sendQueuedNow();
  return result && result.failed === 0 && result.sent > 0 ? 'Sent' : 'Queued';
}
